import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        solid ? "bg-white shadow-[0_1px_0_0_#E5E7EB] py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="w-full max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center shrink-0" aria-label="Meridian Vanguard Solutions home">
          <span className={`flex items-center rounded-md ${solid ? "bg-transparent" : "bg-white/95 px-2.5 py-1.5"} transition-colors`}>
            <img src="/logo.png" alt="Meridian Vanguard Solutions, LLC" className="h-12 md:h-14 w-auto" />
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.path;
            return (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[13px] font-semibold uppercase tracking-[0.08em] transition-colors relative ${
                  solid ? "text-navy hover:text-accent2" : "text-white/90 hover:text-white"
                } ${active ? (solid ? "text-accent2" : "text-white") : ""}`}
              >
                {l.label}
                {active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full bg-accent2" />}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="navbar-cta-contact"
            className="inline-flex items-center rounded-xl bg-navy text-white px-6 py-2.5 text-sm font-semibold hover:bg-accent2 transition-colors duration-300"
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="navbar-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 rounded-md ${solid ? "text-navy" : "text-white"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden bg-white border-t border-hairline px-6 py-6 space-y-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block py-3 text-navy font-semibold uppercase tracking-wide text-sm border-b border-hairline"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-navy text-white px-6 py-3 text-sm font-semibold"
          >
            Contact Us
          </Link>
        </div>
      )}
    </header>
  );
}
